import { AgentStep } from "../../../enums/agent.enum";

/**
 * These constants define bidirectional lookup maps between AgentStep enum values and
 * their URL-safe string representations used in the `:step` route parameter.
 *
 * 1. `AGENT_STEP_SLUG_MAP` (URL param → Enum): Used on component init to read the
 *    `:step` route parameter (e.g., `'api-settings'`) and resolve it to the corresponding
 *    enum value, which is matched to the stepper index to activate the correct step.
 *
 * 2. `AGENT_STEP_TO_SLUG` (Enum → URL param): Used when the user clicks a step in the UI.
 *    The active step's enum value is converted to its slug and written into the URL
 *    via `navigateByUrl`, keeping the address bar in sync so the user can bookmark
 *    or share a direct link to the current step (e.g., `/edit-agent/:id/api-settings`).
 */


/**
 * Maps a URL slug to the corresponding AgentStep label.
 * Used to resolve the active step from the `:step` route parameter on component init.
 *
 * @example
 * AGENT_STEP_SLUG_MAP['api-settings'] // → AgentStep.API_SETTINGS
 */
export const AGENT_STEP_SLUG_MAP: Record<string, AgentStep> = {
  details: AgentStep.DETAILS,
  definition: AgentStep.DEFINITION,
  'api-settings': AgentStep.API_SETTINGS,
  security: AgentStep.SECURITY,
  admin: AgentStep.ADMIN,
};

/**
 * Maps an AgentStep label to its URL slug.
 * Used when the user navigates to a step in the UI to update the URL address bar.
 *
 * Note: The keys in the object are computed property names using the enum values to ensure type safety and maintainability.
 *
 * @example
 * AGENT_STEP_TO_SLUG[AgentStep.API_SETTINGS] // → 'api-settings'
 */
export const AGENT_STEP_TO_SLUG: Record<AgentStep, string> = {
  [AgentStep.DETAILS]: 'details',
  [AgentStep.DEFINITION]: 'definition',
  [AgentStep.API_SETTINGS]: 'api-settings',
  [AgentStep.SECURITY]: 'security',
  [AgentStep.ADMIN]: 'admin',
};

